import { readableStreamToPng } from "../editor/utils/convert-utils";
import { trackEvent } from "../telemetry";
import { defaultThumbnail } from "../defaultScene";
import { compress, decompress } from "../ui/utils/lz4.js";
import configs from "../configs.js";
import { dashboardApiClient } from "./DashboardApiClient";
import { uploadFile } from "../editor/utils/file";
import { normalizeProjectAuthorization } from "../ui/projectAuthorization.js";

function withProjectAuthorization(project) {
  return {
    ...project,
    authorization: normalizeProjectAuthorization(project)
  };
}

export default class ProjectsService {
  constructor(apiClient) {
    this.apiClient = apiClient;
    this.apiKey = configs.PERSISTENCE_API_KEY;
    this.workspaceIdGetter = null; // Will be set by Api class
  }

  /**
   * Set a function to get the current workspace ID from context.
   * @param {Function} getter - Function that returns the current workspace ID
   */
  setWorkspaceIdGetter(getter) {
    this.workspaceIdGetter = getter;
  }

  getWorkspaceId() {
    // Try to get workspace_id from context first (via injected getter)
    if (this.workspaceIdGetter) {
      const workspaceId = this.workspaceIdGetter();
      if (workspaceId) {
        return workspaceId;
      }
    }

    // Fallback to localStorage (for backward compatibility during transition)
    const workspaceId = localStorage.getItem("selected_workspace_id");
    if (!workspaceId) {
      throw new Error("No workspace selected. Please select a workspace first.");
    }
    return workspaceId;
  }

  getBaseUrl() {
    const workspaceId = this.getWorkspaceId();
    return `${configs.CUSTOM_API_URL}/workspaces/${workspaceId}/projects`;
  }

  async exportProject(project, sourceBranchId = null) {
    const fileName = this._getExportFileName(project.name);
    const fileHandle = await this._openSaveFilePicker(fileName);
    if (fileHandle === null) {
      return;
    }

    const { url } = await this.apiClient.getProjectContentUrl(project.project_key, sourceBranchId);
    const response = await this.apiClient.fetch(url, { cache: "no-store" });

    let exportBlob;
    const contentType = response.headers.get("content-type");

    if (contentType === "application/json" || contentType === "text/plain") {
      const json = await response.json();
      exportBlob = await compress(json);
    } else {
      exportBlob = await response.blob();
    }

    if (fileHandle) {
      await this._writeFileWithPicker(fileHandle, exportBlob);
      trackEvent("Project Exported");
      return;
    }

    const localUrl = URL.createObjectURL(exportBlob);
    this._triggerDownload(localUrl, fileName);
    setTimeout(() => URL.revokeObjectURL(localUrl), 10000);
    trackEvent("Project Exported");
  }

  async _openSaveFilePicker(fileName) {
    if (!window?.showSaveFilePicker || !window.isSecureContext) {
      return undefined;
    }

    try {
      return await window.showSaveFilePicker({
        suggestedName: `${fileName}.xrproj`,
        types: [
          {
            description: "XR Project",
            accept: {
              "application/octet-stream": [".xrproj"]
            }
          }
        ]
      });
    } catch (error) {
      if (error?.name === "AbortError") {
        return null;
      }

      return undefined;
    }
  }

  async _writeFileWithPicker(fileHandle, blob) {
    const writable = await fileHandle.createWritable();
    await writable.write(blob);
    await writable.close();
  }

  _getExportFileName(name) {
    return name.toLowerCase().replace(/\s+/g, "-");
  }

  _triggerDownload(url, fileName) {
    const el = document.createElement("a");
    el.download = fileName + ".xrproj";
    el.href = url;
    el.target = "_blank";
    document.body.appendChild(el);
    el.click();
    document.body.removeChild(el);
  }

  async importProject(blob, name = "New Project") {
    let serializedProject;
    const fileName = blob.name || "";
    let text;
    if (fileName.endsWith(".xrproj")) {
      serializedProject = await decompress(blob);
    } else {
      text = await blob.text();
      try {
        serializedProject = JSON.parse(text);
      } catch (e) {
        throw new Error("Invalid JSON file: " + e.message);
      }
    }

    const projectBlob = new Blob([JSON.stringify(serializedProject)], {
      type: "application/json"
    });

    const { body: thumbnailStream } = await fetch(defaultThumbnail());
    const thumbnailBlob = await readableStreamToPng(thumbnailStream);
    const thumbnailFile = new File([thumbnailBlob], "thumbnail.png", { type: "image/png" });

    const appName = configs.APP_NAME || configs.REACT_APP_NAME || "onland";
    const folder = `assets360/${appName}/projects`;

    const thumbnailEntry = await uploadFile({
      file: thumbnailFile,
      folder,
      asset_type: "project",
      useWorkspaceAssets: true
    });

    const projectFile = new File([projectBlob], "scene.mud", { type: "application/json" });
    const projectEntry = await uploadFile({
      file: projectFile,
      folder,
      asset_type: "project",
      useWorkspaceAssets: true
    });

    // Use the name from the scene if available
    const projectName = serializedProject.metadata?.name || serializedProject.name || name;
    const payload = {
      name: projectName,
      screenshot_resource_id: thumbnailEntry.uuid,
      model_resource_id: projectEntry.uuid
    };

    const json = await dashboardApiClient.postJson("projects", payload);
    this.apiClient.emit && this.apiClient.emit("project-saved");

    // API returns { project: {...} }, extract the project data
    return withProjectAuthorization(json.project || json.data || json);
  }

  async adminGetProjects(
    { page = 1, perPage = 20, search = "", orderBy = "created_at", orderDirection = "desc" } = {},
    { signal } = {}
  ) {
    const params = {
      page: page.toString(),
      per_page: perPage.toString(),
      order_by: orderBy,
      order_direction: orderDirection
    };
    if (search) params.search = search;

    const json = await dashboardApiClient.getJson("admin/projects", params, false, signal);
    return json; // { data: [...], meta: { last_page, total, current_page } }
  }

  async adminDeleteProject(projectId) {
    await dashboardApiClient.deleteJson(`admin/projects/${projectId}`, false);
  }

  async getProjects(page = 1, perPage = 9999) {
    const json = await dashboardApiClient.getJson("projects", {
      page: page.toString(),
      per_page: perPage.toString()
    });

    // Laravel ResourceCollection returns: { data: [...], links: {...}, meta: {...} }
    if (!json.data || !Array.isArray(json.data)) {
      throw new Error(`Error fetching projects: ${json.error || "Invalid response format."}`);
    }

    // Filter out projects that are templates and return as array (for compatibility with existing code)
    return json.data
      .filter(project => !project.is_template)
      .map(withProjectAuthorization);
  }
}
